import React, { useState, useRef } from "react";
import { resolveMediaUrl } from "services/utils/mediaUrl";

const ImageUpload = ({
  collapsed,
  isSchool,
  handleButtonClick,
  fileInputRef,
  handleFileChange,
  school_logo,
  school_name,
  full_name,
}: any) => {
  const resolvedSchoolLogo = resolveMediaUrl(school_logo);
  const [showImageFallback, setShowImageFallback] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const cropperRef = useRef<HTMLDivElement>(null);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const cropSize = 400;
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);

  // Handle the file selection
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();

      reader.onload = (event) => {
        if (event.target) {
          setImageToCrop(event.target.result as string);
          setShowCropper(true);

          // Reset crop position and scale
          setCropPosition({ x: 0, y: 0 });
          setScale(1);

          // Preload image to get dimensions
          const img = new Image();
          img.onload = () => {
            setImageSize({ width: img.width, height: img.height });
          };
          img.src = event.target.result as string;
        }
      };

      reader.readAsDataURL(file);
    }
  };

  // Start drag operation
  const handleMouseDown = (e: React.MouseEvent) => {
    if (imageToCrop) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - cropPosition.x, y: e.clientY - cropPosition.y });
    }
  };

  // Update position during drag
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && cropperRef.current) {
      const maxX = (imageSize.width * scale - cropSize) / 2;
      const maxY = (imageSize.height * scale - cropSize) / 2;

      let newX = e.clientX - dragStart.x;
      let newY = e.clientY - dragStart.y;

      // Constrain within image bounds
      newX = Math.max(-maxX, Math.min(newX, maxX));
      newY = Math.max(-maxY, Math.min(newY, maxY));

      setCropPosition({ x: newX, y: newY });
    }
  };

  // End drag operation
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle zoom/scale change
  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScale(parseFloat(e.target.value));
  };

  // Apply the crop and close the cropper
  const applyCrop = () => {
    if (!imageToCrop || !cropperRef.current) return;

    // Create a canvas to draw the cropped image
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    // Set canvas size to crop size
    canvas.width = cropSize;
    canvas.height = cropSize;

    // Create an image element from our source
    const img = new Image();
    img.onload = () => {
      // Calculate source rectangle
      const sourceX = imageSize.width / 2 - cropPosition.x / scale;
      const sourceY = imageSize.height / 2 - cropPosition.y / scale;
      const sourceSize = cropSize / scale;

      // Draw the cropped portion to the canvas
      ctx.drawImage(
        img,
        sourceX - sourceSize / 2,
        sourceY - sourceSize / 2,
        sourceSize,
        sourceSize,
        0,
        0,
        cropSize,
        cropSize
      );

      // Convert canvas to blob and trigger the original file change handler
      canvas.toBlob(
        (blob) => {
          if (blob && handleFileChange) {
            // Create a new File object
            const croppedFile = new File([blob], "cropped-image.jpg", { type: "image/jpeg" });

            // Create a dummy event object with the file
            const dummyEvent = {
              target: {
                files: [croppedFile],
              },
            };

            // Call the original handler
            handleFileChange(dummyEvent);
          }

          // Close the cropper
          setShowCropper(false);
          setImageToCrop(null);
        },
        "image/jpeg",
        0.95
      );
    };

    img.src = imageToCrop;
  };

  // Cancel cropping
  const cancelCrop = () => {
    setShowCropper(false);
    setImageToCrop(null);
  };

  return (
    <>
      <div
        className={`rounded-full md:border-[9px] border-[6px] border-coral transition-all ease-in duration-700 ${
          collapsed ? "md:w-16 w-12 md:h-16 h-12" : "w-[140px] h-[140px]"
        } relative`}
      >
        {isSchool ? (
          <button className="absolute bottom-0 right-0" onClick={handleButtonClick}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`transition-all ease-in duration-700 ${collapsed ? "w-0 h-0" : "w-10 h-10"}`}
              viewBox="0 0 59 58"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M29.5002 53.1668C42.8469 53.1668 53.6668 42.3469 53.6668 29.0002C53.6668 15.6533 42.8469 4.8335 29.5002 4.8335C16.1533 4.8335 5.3335 15.6533 5.3335 29.0002C5.3335 42.3469 16.1533 53.1668 29.5002 53.1668ZM31.3127 21.7502C31.3127 20.7492 30.5011 19.9377 29.5002 19.9377C28.4992 19.9377 27.6877 20.7492 27.6877 21.7502V27.1877H22.2502C21.2492 27.1877 20.4377 27.9992 20.4377 29.0002C20.4377 30.0011 21.2492 30.8127 22.2502 30.8127H27.6877V30.8127H36.7502C37.7511 30.8127 38.5627 30.0011 38.5627 29.0002C38.5627 27.9992 37.7511 27.1877 36.7502 27.1877H31.3127V21.7502Z"
                fill="white"
              />
            </svg>
          </button>
        ) : null}

        <input type="file" ref={fileInputRef} onChange={handleFile} style={{ display: "none" }} accept="image/*" />
        {isSchool && resolvedSchoolLogo && !showImageFallback ? (
          <img
            src={resolvedSchoolLogo}
            alt=""
            className="w-full h-full rounded-full object-cover"
            onError={() => setShowImageFallback(true)}
          />
        ) : (
          <div className="w-full h-full rounded-full bg-coral flex justify-center items-center text-white text-2xl uppercase">
            {isSchool ? (school_name ? school_name[0] : null) : full_name ? full_name[0] : null}
          </div>
        )}
      </div>

      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full">
            <h2 className="text-xl font-bold mb-4">Crop Your Image</h2>

            <div
              className="relative w-full h-96 overflow-hidden bg-gray-100 mb-4 flex items-center justify-center"
              ref={cropperRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Background image (scaled and positioned) */}
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={imageToCrop}
                  alt="To crop"
                  style={{
                    transform: `translate(${cropPosition.x}px, ${cropPosition.y}px) scale(${scale})`,
                    maxWidth: "none",
                    maxHeight: "none",
                    objectFit: "contain",
                    aspectRatio: "1/1",
                  }}
                  draggable="false"
                />
              </div>

              {/* Crop overlay */}
              <div
                style={{
                  position: "absolute",
                  width: `${cropSize}px`,
                  height: `${cropSize}px`,
                  border: "2px dashed white",
                  borderRadius: "50%",
                  boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
                  cursor: isDragging ? "grabbing" : "grab",
                  aspectRatio: "1/1",
                }}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Zoom</label>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={scale}
                onChange={handleZoomChange}
                className="w-full"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button onClick={cancelCrop} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Cancel
              </button>
              <button onClick={applyCrop} className="px-4 py-2 bg-coral text-white rounded hover:bg-opacity-90">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageUpload;